// frontend/src/components/asset/DeactivateAccessModal.js
import React from 'react';
import { ShieldOff } from 'lucide-react';
import Modal from '../UI/Modal';
import { formatDate } from '../../utils/dateUtils';

/**
 * Confirmation dialog for the one-click "Deactivate access" action on
 * access-control credentials (door access / facial recognition). Spells out the
 * consequences because this revokes access and unassigns the holder in one step.
 *
 * Shared by the asset-admin and HR-portal asset lists so both behave identically.
 */
const DeactivateAccessModal = ({ isOpen, onClose, asset, onConfirm, isLoading }) => {
  if (!asset) return null;
  const endDate = asset.current_assignee_employment_end_date;
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Deactivate Access"
      size="sm"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline">
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="btn btn-danger"
          >
            {isLoading ? 'Deactivating…' : 'Deactivate access'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
            <ShieldOff className="w-5 h-5 text-red-600" />
          </div>
          <p className="text-sm text-gray-700">
            Revoke <span className="font-medium">{asset.asset_tag}</span>
            {asset.current_assignee_name ? (
              <> held by <span className="font-medium">{asset.current_assignee_name}</span>
                {asset.current_assignee_staff_id ? ` (${asset.current_assignee_staff_id})` : ''}</>
            ) : null}
            ?
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600 space-y-1">
          <p className="font-medium text-gray-700">This will:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Set the credential to <span className="font-medium">Deactivated</span></li>
            <li>
              Record the deactivation{' '}
              {endDate ? <>effective <span className="font-medium">{formatDate(endDate)}</span></> : 'as of now'}
            </li>
            <li>Unassign it from the holder</li>
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default DeactivateAccessModal;
